import { Pipe, PipeTransform } from '@angular/core';

import { CustomDataOperators } from '@dpr-details/enums';
import { DprActivityCustomDataGridData } from '@dpr-details/models/grid';
import { DataType } from '@shared/models';
import {
  CustomData,
  CustomDataFormulaConfiguration,
} from '@shared/models/project-setup';

@Pipe({
  name: 'customDataFormula',
})
export class CustomDataFormulaPipe implements PipeTransform {
  transform(
    customDataFormulaConfiguration: CustomDataFormulaConfiguration,
    dprActivityCustomDataList: DprActivityCustomDataGridData[],
    activityId: number | string,
    customDataList: CustomData[]
  ): string {
    const customDataOperatorId =
      customDataFormulaConfiguration.customDataOperatorId;
    const leftOperandCustomDataId =
      customDataFormulaConfiguration.leftOperandCustomDataId;
    const rightOperandCustomDataId =
      customDataFormulaConfiguration.rightOperandCustomDataId;
    const dataTypeId = customDataFormulaConfiguration.dataTypeId;
    const decimalCount = customDataFormulaConfiguration.decimalCount;

    if (
      !activityId ||
      !customDataOperatorId ||
      (!leftOperandCustomDataId && !rightOperandCustomDataId) ||
      !dprActivityCustomDataList?.length
    ) {
      return null;
    }

    const leftOperandCustomData = dprActivityCustomDataList.find(
      (x) => x.customDataId === leftOperandCustomDataId
    );

    const rightOperandCustomData = dprActivityCustomDataList.find(
      (x) => x.customDataId === rightOperandCustomDataId
    );

    if (
      leftOperandCustomData?.value == null &&
      rightOperandCustomData?.value == null
    ) {
      return null;
    }

    const leftOperandDataType = this.getDataType(
      leftOperandCustomData?.customDataId,
      customDataList
    );
    const rightOperandDataType = this.getDataType(
      rightOperandCustomData?.customDataId,
      customDataList
    );

    const formattedLeftOperand = this.formatOperand(
      leftOperandCustomData,
      leftOperandDataType
    );
    const formattedRightOperand = this.formatOperand(
      rightOperandCustomData,
      rightOperandDataType
    );

    let result: number = null;

    switch (customDataOperatorId) {
      case CustomDataOperators.Add:
        result = formattedLeftOperand + formattedRightOperand;
        break;
      case CustomDataOperators.Subtract:
        result = formattedLeftOperand - formattedRightOperand;
        break;
      case CustomDataOperators.Multiply:
        result = formattedLeftOperand * formattedRightOperand;
        break;
      case CustomDataOperators.Divide:
        if (formattedRightOperand === 0) {
          return '-';
        }

        result = formattedLeftOperand / formattedRightOperand;
        break;
      default:
        return null;
    }

    return this.formatResult(result, dataTypeId, decimalCount);
  }

  private formatOperand(
    customData: DprActivityCustomDataGridData,
    dataType: DataType
  ): number {
    // If the operand does not have a value, it is used as being 0 by default
    if (customData?.value == null) {
      return 0;
    }

    if (dataType === DataType.Percentage) {
      return +customData.value / 100;
    }

    return +customData.value;
  }

  // TODO - After clarifying the issue with dataTypeId property from DprActivityCustomDataGridData, this method may be redundant
  private getDataType(
    customDataId: number,
    customDataList: CustomData[]
  ): DataType {
    return customDataList.find((x) => x.id === customDataId)?.dataTypeId;
  }

  private formatResult(
    rawResult: number,
    dataType: DataType,
    decimalCount: number
  ): string {
    return dataType === DataType.Number
      ? rawResult.toFixed(decimalCount)
      : (rawResult * 100).toFixed(decimalCount) + '%';
  }
}
